import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CardMedia from "@mui/material/CardMedia";
import Rating from "@mui/material/Rating";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";

import { capitalCase } from "capital-case";
import NumberFormat from "react-number-format";
import { Carousel } from "antd";
import moment from "moment";
import Checkbox from "@mui/material/Checkbox";

import StyleDetailModal from "pages/de/styles/detail/components/StyleDetailModal";

import curved9 from "assets/images/upstyled/default_background.png";
import colors from "assets/theme/base/colors";

import CategoryContext from "context/category-context";
import {
  canResizeImageUrl,
  getStyleImages,
  getFileImage,
  getOnLineTechPackImages,
} from "utils/imageHelper";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

function StyleCard2({
  product,
  size = "md",
  sx = {},
  detailUrl = null,
  handleSelectStyle,
  isSelected,
  imageMode = "normal",
  metaFilesKey = ["imageFiles", "techPacksFiles", "referenceFiles"],
  key,
  noSlider = false,
}) {
  const [detailViewVisible, setDetailViewVisible] = useState(false);
  const [categoryLabel, setCategoryLabel] = useState(""); //Using Category Name if the category ID was found at category tree

  const {
    tree,
    actions: { findNodeByID },
  } = useContext(CategoryContext);

  useEffect(() => {
    let category = "No Category";
    if (product?.type) {
      category = product.type?.value;
    } else if (product?.type_id) {
      const categoryNode = findNodeByID(tree, product.type_id);
      category = categoryNode?.label;
    }
    setCategoryLabel(category);
  }, [product]);

  const styleMeta = product?.style ? product.style : {};

  let styleImages = getStyleImages(product, false, metaFilesKey);

  const styleNumber = styleMeta?.style_number ? styleMeta.style_number : "N/A";

  let styles = {
    cardWidth: 260,
    cardHeight: 425,
    imgHeight: 220,
    titleMinHeight: "34px",
    titleFontSize: "h6",
    textFontSize: "body1",
  };
  if (size == "sm") {
    styles = {
      cardWidth: 220,
      cardHeight: 375,
      imgHeight: 180,
      titleMinHeight: "26px",
      titleFontSize: "h6",
      textFontSize: "body1",
    };
  }

  const ListRow = ({ label, value, divider = false }) => {
    return (
      <SuiBox px={1} sx={{ overflow: "hidden" }}>
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
          gap={1}
        >
          <SuiTypography textTransform="capitalize" sx={{ flex: "none", minWidth: "min-content" }}>
            {label}
          </SuiTypography>

          <SuiBox>
            <Tooltip title={value}>
              <SuiTypography
                color="info"
                textTransform="capitalize"
                lineHeight="1"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                  wordBreak: "break-all",
                }}
              >
                {value}
              </SuiTypography>
            </Tooltip>
          </SuiBox>
        </SuiBox>
        {divider ? <Divider sx={{ backgroundColor: colors.dark.main, margin: "4px 2px" }} /> : null}
      </SuiBox>
    );
  };

  if (noSlider) {
    styleImages = styleImages.slice(0, 1);
  }

  return (
    <Card
      sx={{
        my: 2,
        mx: 1,
        width: styles.cardWidth,
        minWidth: styles.cardWidth,
        height: styles.cardHeight,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        ...sx,
      }}
    >
      <Carousel
        arrows={true}
        dots={false}
        key={key}
        prevArrow={
          <div>
            <button
              class="react-multiple-carousel__arrow react-multiple-carousel__arrow--left "
              type="button"
            ></button>
          </div>
        }
        nextArrow={
          <div>
            <button
              class="react-multiple-carousel__arrow react-multiple-carousel__arrow--right "
              type="button"
            ></button>
          </div>
        }
      >
        {styleImages.length > 0 ? (
          styleImages.map((img) => (
            <SuiBox
              sx={{ cursor: "pointer", overflow: "hidden", margin: "0 auto" }}
              onClick={handleSelectStyle ? () => handleSelectStyle(!isSelected) : null}
              borderRadius="xl"
              {...(handleSelectStyle
                ? {}
                : { component: detailUrl ? Link : "div", to: detailUrl ? detailUrl : null })}
            >
              <ImageCardWrapper data={img} height={styles.imgHeight} width={styles.cardWidth} />
            </SuiBox>
          ))
        ) : (
          <SuiBox
            sx={{ cursor: "pointer", overflow: "hidden", margin: "0 auto" }}
            onClick={handleSelectStyle ? () => handleSelectStyle(!isSelected) : null}
            borderRadius="xl"
            {...(handleSelectStyle
              ? {}
              : { component: detailUrl ? Link : "div", to: detailUrl ? detailUrl : null })}
          >
            <SuiBox
              sx={{
                overflow: "hidden",
                backgroundImage: ({
                  functions: { rgba, linearGradient },
                  palette: { gradients },
                }) =>
                  `${linearGradient(
                    rgba(gradients.secondary.main, 0.2),
                    rgba(gradients.secondary.state, 0.4)
                  )}`,
                height: styles.imgHeight,
                width: styles.cardWidth,
                margin: 0,
                boxShadow: ({ boxShadows: { md } }) => md,
                objectFit: "contain",
                objectPosition: "center",
              }}
            />{" "}
          </SuiBox>
        )}
      </Carousel>
      <SuiBox sx={{ position: "absolute", right: 10, top: 10 }}>
        <Tooltip title={styleNumber}>
          <SuiBadge
            variant="contained"
            color="secondary"
            badgeContent={
              <SuiTypography
                variant={styles.textFontSize}
                textTransform="none"
                color="secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: "1",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {styleNumber}
              </SuiTypography>
            }
          />
        </Tooltip>
      </SuiBox>
      <SuiBox py={1} px={1}>
        <div>
          <SuiBox display="flex" justifyContent="space-between" alignItems="top">
            <Tooltip title={product.title}>
              {handleSelectStyle ? (
                <SuiBox onClick={() => handleSelectStyle(!isSelected)} sx={{ cursor: "pointer" }}>
                  <SuiTypography
                    variant={styles.titleFontSize}
                    fontWeight="medium"
                    lineHeight="1.2"
                    sx={{
                      minHeight: styles.titleMinHeight,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {product.title}
                  </SuiTypography>
                </SuiBox>
              ) : (
                <SuiBox
                  // onClick={detailUrl ? null : () => setDetailViewVisible(true)}
                  component={detailUrl ? Link : "div"}
                  to={detailUrl ? detailUrl : null}
                  sx={{ cursor: "pointer" }}
                >
                  <SuiTypography
                    variant={styles.titleFontSize}
                    fontWeight="medium"
                    lineHeight="1.2"
                    sx={{
                      minHeight: styles.titleMinHeight,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {product.title}
                  </SuiTypography>
                </SuiBox>
              )}
            </Tooltip>
            {handleSelectStyle ? (
              <Checkbox onChange={handleSelectStyle} checked={isSelected} />
            ) : null}
          </SuiBox>

          <ListRow label="Collection" value={product?.collection?.title || "N/A"} divider={true} />
          <ListRow label="Brand" value={styleMeta?.brand || "N/A"} divider={true} />
          <ListRow label="Season" value={styleMeta?.season?.label || "N/A"} divider={true} />
          <ListRow
            label="Year"
            value={styleMeta?.year ? moment(styleMeta.year).format("YYYY") : "N/A"}
            divider={true}
          />
          <ListRow label="Category" value={categoryLabel} divider={false} />
        </div>
      </SuiBox>
      {detailViewVisible ? (
        <StyleDetailModal
          visible={detailViewVisible}
          handleClose={() => setDetailViewVisible(false)}
          styleId={product.id}
        />
      ) : null}
    </Card>
  );
}

export default StyleCard2;
